/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Tamagui doesn't require style imports

import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import EnhancedApp from "./components/enhanced-app/EnhancedApp";
import reportWebVitals from "./reportWebVitals";
import { ClerkProvider } from "@clerk/clerk-react";
import { PostHogProvider } from "posthog-js/react";
import { AnalyticsProvider } from "./components/analytics/AnalyticsProvider";
import { initWebVitals } from "./lib/web-vitals";

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const POSTHOG_KEY =
  import.meta.env.VITE_POSTHOG_KEY || "phc_KNfFzVhXdtLGooSSNo1dtqBDK2hTv0P8aj3qIvDncbn";
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";

// Check if services should be disabled for development
const DISABLE_POSTHOG = import.meta.env.VITE_DISABLE_POSTHOG === "true";
const DISABLE_CLERK = import.meta.env.VITE_DISABLE_CLERK === "true";

if (!DISABLE_CLERK && !CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

// PostHog configuration options
const posthogOptions = {
  api_host: POSTHOG_HOST,
  person_profiles: "identified_only" as const, // Only track identified users to respect privacy
  capture_pageview: true,
  capture_pageleave: true,
  autocapture: false, // Disable automatic event capture to only track intentional events
};

// Check for enhanced mode (3D visualization)
const urlParams = new URLSearchParams(window.location.search);
const enhancedMode =
  urlParams.get("enhanced") === "true" || localStorage.getItem("flowcus_enhanced_mode") === "true";

// Use Enhanced App with 3D visualization if enabled
const AppComponent = enhancedMode ? EnhancedApp : App;

// Create the app with conditional providers
const createApp = () => {
  let app = (
    <AnalyticsProvider>
      <AppComponent />
    </AnalyticsProvider>
  );

  // Wrap with Clerk if not disabled
  if (!DISABLE_CLERK && CLERK_PUBLISHABLE_KEY) {
    app = <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>{app}</ClerkProvider>;
  }

  // Wrap with PostHog if not disabled
  if (!DISABLE_POSTHOG) {
    app = (
      <PostHogProvider apiKey={POSTHOG_KEY} options={posthogOptions}>
        {app}
      </PostHogProvider>
    );
  }

  return app;
};

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(<React.StrictMode>{createApp()}</React.StrictMode>);

// Register service worker for PWA functionality
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then(registration => {
        // Service worker registered successfully

        // Check for updates to the service worker
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                // New content is available, prompt user to refresh
                if (window.confirm("New version available! Refresh to update?")) {
                  window.location.reload();
                }
              }
            });
          }
        });
      })
      .catch(registrationError => {
        // Service worker registration failed - silently continue
      });
  });
}

// Initialize Web Vitals monitoring
initWebVitals();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
